import { useState, useEffect, useRef } from 'react'
import { Loader2, Package, ArrowRight, BarChart2 } from 'lucide-react'
import Navbar from './components/Navbar'
import LandingView from './components/landing/LandingView'
import RunsView from './components/runs/RunsView'
import DesignerView from './components/designer/DesignerView'
import StandardView from './components/standard/StandardView'
import GuidedUploadView from './components/guided/GuidedUploadView'
import GuidedAnalyzeView from './components/guided/GuidedAnalyzeView'
import IdmsAnalyzeView from './components/guided/IdmsAnalyzeView'
import GenerateDocsView from './components/docs/GenerateDocsView'
import ExtractBizLogicView from './components/bizlogic/ExtractBizLogicView'
import DecomposeCodeView from './components/decompose/DecomposeCodeView'
import GuidedServiceGenerationView from './components/guided/GuidedServiceGenerationView'
import GuidedJavaPackagingView from './components/guided/GuidedJavaPackagingView'
import JpkgReportMockView from './components/guided/JpkgReportMockView'
import ReportsView from './components/reports/ReportsView'
import MCPView from './components/views/MCPView'
import ConversionScopeView from './components/views/ConversionScopeView'
import WorkspaceView from './components/views/WorkspaceView'
import RefactorCodeView from './components/refactor/RefactorCodeView'
import ModelTrainingView from './components/training/ModelTrainingView'
import EtlDesignerView from './components/etl/EtlDesignerView'
import SchemaToolView from './components/schema/SchemaToolView'
import JobMonitorView from './components/tools/JobMonitorView'
import RefineCodeView from './components/refine/RefineCodeView'
import RefineCodeWorkbenchMock from './components/refine/RefineCodeWorkbenchMock'
import RefineCodeWorkspace from './components/refine/RefineCodeWorkspace'
import PipelineRunsView from './components/pipeline-runs/PipelineRunsView'
import SettingsModal from './components/shared/SettingsModal'
import S3BrowserModal from './components/shared/S3BrowserModal'
import WorkerHealthBanner from './components/shared/WorkerHealthBanner'
import HelpPanel from './help/HelpPanel'
import { useSettings } from './hooks'
import { getAPIBaseUrl } from './environment.js'
import './App.css'

// Open Reports in a new tab with optional deep-link context
const openReports = ({ accountId = '', appName = '', runId = '', stage = '' } = {}) => {
  const params = new URLSearchParams({ standalone: 'reports' })
  if (accountId) params.set('account_id', accountId)
  if (appName)   params.set('app_name', appName)
  if (runId)     params.set('run_id', runId)
  if (stage)     params.set('stage', stage)
  window.open(
    `${window.location.origin}${window.location.pathname}?${params}`,
    '_blank'
  )
}

function AppInner() {
  const [currentProject, setCurrentProject] = useState(null)
  const [currentRun, setCurrentRun] = useState(null)   // null = show RunsView; set = show workflow
  const [mode, setMode] = useState('workflow')
  const [guidedIngestResult, setGuidedIngestResult] = useState(null)  // held between upload→analyze→standard
  const [docCaJobId, setDocCaJobId] = useState(null)  // CA job ID threaded into Generate Documentation screen
  const [dcApprovedJobId, setDcApprovedJobId] = useState(null)  // decompose_code job ID passed to Service Generation stage
  const [svcGenJobIds,   setSvcGenJobIds]   = useState([])     // Journey: SvcGen fan-out job IDs passed to Journey JPkg stage
  const [rfneJobId,      setRfneJobId]      = useState(null)   // Refine Code job ID threaded into Journey JPkg
  const [jpkgShowReport, setJpkgShowReport] = useState(false) // Journey JPkg: show mock quality report
  const [pfltJobId,      setPfltJobId]      = useState(null)  // Pre-Flight job ID threaded into Journey JPkg
  const [showRunsOverview, setShowRunsOverview] = useState(false)
  const [runsAccountId, setRunsAccountId]       = useState(null)
  const [showSettings, setShowSettings] = useState(false)
  const [showS3Browser, setShowS3Browser] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [workflowActions, setWorkflowActions] = useState({ run: null, save: null })
  // State-aware routing: true while we probe runs + CA endpoints to pick the right destination
  const [projectRouting, setProjectRouting] = useState(false)
  const routingRef = useRef(false)  // prevent stale-closure double-fire
  // Project card error: project name that has no analysis data (shown on card)
  const [projectError, setProjectError] = useState(null)

  // Use the real settings hook for theme and profile
  const { profile, theme, loading } = useSettings()

  // Apply theme when it changes
  useEffect(() => {
    if (theme) {
      console.log('[App] Applying theme:', theme)
      document.documentElement.setAttribute('data-theme', theme)
    }
  }, [theme])

  // State-aware routing: when a project is clicked, probe runs + CA endpoints
  // to choose the right destination instead of always dropping into RunsView.
  useEffect(() => {
    if (!projectRouting || !currentProject) return
    if (routingRef.current) return
    routingRef.current = true

    const API_BASE = window.__MODERNIZEIT_API_URL__ || getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = token ? { Authorization: `Bearer ${token}` } : {}
    const account  = encodeURIComponent(currentProject.account || '')
    const appName  = encodeURIComponent(currentProject.name || '')

    console.log('[App] State-aware routing: probing project state for', currentProject.name, '(account:', currentProject.account, ')')

    const runsPromise = fetch(
      `${API_BASE}/workflows/runs?account_id=${account}&app_name=${appName}&limit=5`,
      { headers }
    ).then(r => r.ok ? r.json() : null).catch(() => null)

    const caPromise = fetch(
      `${API_BASE}/codeanalysis/latest-job?account_id=${account}&app_name=${appName}`,
      { headers }
    ).then(r => r.ok ? r.json() : null).catch(() => null)

    // Also probe for IDMS analysis data
    const idmsPromise = fetch(
      `${API_BASE}/idmsanalysis/latest-job?account_id=${account}&app_name=${appName}`,
      { headers }
    ).then(r => r.ok ? r.json() : null).catch(() => null)

    Promise.all([runsPromise, caPromise, idmsPromise]).then(([runsData, caData, idmsData]) => {
      const hasRuns = Array.isArray(runsData?.runs) && runsData.runs.length > 0
      const hasCA   = !!(caData?.job_id)
      const hasIdms = !!(idmsData?.job_id)

      console.log('[App] Routing result for', currentProject.name, '→ hasRuns:', hasRuns, '| hasCA:', hasCA, '| hasIdms:', hasIdms)

      if (hasIdms) {
        // Has IDMS analysis data → go to IDMS Analyze page
        console.log('[App] Route → IdmsAnalyzeView (IDMS job:', idmsData.job_id, ')')
        setGuidedIngestResult(null)
        setCurrentRun({ _isAnalyze: true, _isIdms: true, _fromProjectCard: true })
        setMode('idms-analyze')
      } else if (hasCA) {
        // Has code analysis data → go straight to Analyze page
        console.log('[App] Route → GuidedAnalyzeView (CA job:', caData.job_id, ')')
        setGuidedIngestResult(null)          // null → discovery fallback in GuidedAnalyzeView
        setCurrentRun({ _isAnalyze: true, _fromProjectCard: true }) // _fromProjectCard: Back goes to Landing
        setMode('guided-analyze')
      } else {
        // No analysis data yet → show RunsView (user can start a new run)
        console.log('[App] Route → RunsView (no CA data for', currentProject.name, ')')
        setCurrentRun(null)
        setMode('workflow')
      }

      setProjectRouting(false)
      routingRef.current = false
    })
  }, [projectRouting, currentProject])

  // Runs button from LandingView → show account-wide pipeline run history
  const handleRunsClick = (accountId) => {
    setRunsAccountId(accountId)
    setShowRunsOverview(true)
  }

  // Project selected from LandingView → begin state-aware routing probe
  const handleProjectSelect = (project) => {
    routingRef.current = false   // reset guard for new selection
    setCurrentProject(project)
    setCurrentRun(null)
    setMode('workflow')
    setProjectRouting(true)
  }

  // Run selected from RunsView → go to workflow
  const handleRunSelect = (run, targetMode) => {
    setCurrentRun(run)
    setMode(targetMode || 'workflow')
  }

  // Start a fresh run from RunsView — respects Standard vs Advanced mode from NewRunModal
  const handleNewRun = (runData) => {
    setCurrentRun({ _isNew: true, ...(runData || {}) })
    setMode(runData?.mode === 'standard' ? 'standard' : 'workflow')
  }

  // Back from workflow → return to RunsView
  const handleBackToRuns = () => {
    setCurrentRun(null)
    setMode('workflow')
  }

  // Back from RunsView → return to LandingView
  const handleBackToLanding = () => {
    setCurrentProject(null)
    setCurrentRun(null)
    setGuidedIngestResult(null)
    setProjectRouting(false)
    routingRef.current = false
    setMode('workflow')   // always reset mode so no stale guided state persists
  }

  // AI-assisted guided start: skip RunsView, go straight to upload
  const handleGuidedStart = (project) => {
    console.log('[App] Guided start for project:', project?.name)
    setCurrentProject(project)
    setCurrentRun({ _isNew: true, display_name: 'Guided Run', mode: 'guided' })
    setMode('guided')
  }

  // Open Guided Analyze from RunsView — project already selected, discover CA job via fallback
  // Probes for IDMS analysis first; if found, routes to IdmsAnalyzeView instead
  const handleOpenGuidedAnalyze = () => {
    console.log('[App] Opening Guided Analyze from RunsView for project:', currentProject?.name, 'account:', currentProject?.account)
    setGuidedIngestResult(null)
    setCurrentRun({ _isAnalyze: true })  // non-null prevents RunsView from showing

    // Probe for IDMS analysis — if this project has IDMS data, route to IDMS view
    const API_BASE = window.__MODERNIZEIT_API_URL__ || getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = token ? { Authorization: `Bearer ${token}` } : {}
    const account  = encodeURIComponent(currentProject?.account || '')
    const appName  = encodeURIComponent(currentProject?.name || '')

    fetch(`${API_BASE}/idmsanalysis/latest-job?account_id=${account}&app_name=${appName}`, { headers })
      .then(r => r.ok ? r.json() : null)
      .catch(() => null)
      .then(idmsData => {
        if (idmsData?.job_id) {
          console.log('[App] IDMS analysis found (job:', idmsData.job_id, ') → routing to IdmsAnalyzeView')
          setCurrentRun({ _isAnalyze: true, _isIdms: true })
          setMode('idms-analyze')
        } else {
          console.log('[App] No IDMS analysis → routing to standard GuidedAnalyzeView')
          setMode('guided-analyze')
        }
      })
  }

  // Back from GuidedAnalyzeView when opened from RunsView → return to RunsView, not Landing
  const handleGuidedAnalyzeBackToRuns = () => {
    console.log('[App] Back from Guided Analyze → returning to RunsView for:', currentProject?.name)
    setCurrentRun(null)
    setGuidedIngestResult(null)
    setMode('workflow')
    // currentProject stays set → App renders RunsView
  }

  // Upload + auto-run code analysis complete → show Guided Analyze with real results
  // ingestResult now contains jobs.code_analysis_job_id set by GuidedUploadView
  // after it polled the CA job to completion. GuidedAnalyzeView uses this directly.
  const handleGuidedUploadComplete = (ingestResult) => {
    console.log('[App] Guided upload+analysis complete, transitioning to GuidedAnalyzeView:', {
      source_hash:          ingestResult?.source_hash,
      file_count:           ingestResult?.file_count,
      ingest_job_id:        ingestResult?.jobs?.ingest_job_id,
      code_analysis_job_id: ingestResult?.jobs?.code_analysis_job_id,
    })
    setGuidedIngestResult(ingestResult)
    setMode('guided-analyze')
  }

  // Confirm Analysis from GuidedAnalyzeView → transition to StandardView with preloaded ingest
  const handleGuidedAnalysisConfirm = () => {
    const ingestResult = guidedIngestResult
    console.log('[App] Analysis confirmed, transitioning to StandardView:', {
      source_hash: ingestResult?.source_hash,
      file_count: ingestResult?.file_count,
    })
    setCurrentRun(prev => ({
      ...prev,
      _isNew: true,
      display_name: prev?.display_name || 'Guided Run',
      autoStart: true,  // Signal StandardView to auto-start the pipeline
      preloadedIngest: {
        sourceHash: ingestResult?.source_hash,
        ingestJobId: ingestResult?.jobs?.ingest_job_id,
        filesCount: ingestResult?.file_count || null,
      },
    }))
    setGuidedIngestResult(null)
    setMode('standard')
  }

  const renderView = () => {
    switch (mode) {
      case 'standard':
        return (
          <StandardView
            project={currentProject}
            run={currentRun}
            onBack={handleBackToRuns}
            onBackToProjects={handleBackToLanding}
            onSwitchToAdvanced={() => setMode('workflow')}
            onOpenSettings={() => setShowSettings(true)}
            onOpenHelp={() => setShowHelp(true)}
            userProfile={profile}
          />
        )
      case 'workflow':
        return (
          <DesignerView
            project={currentProject}
            run={currentRun}
            onBackToLanding={handleBackToLanding}
            onRegisterActions={setWorkflowActions}
          />
        )
      case 'scope':
        return (
          <ConversionScopeView
            applicationName={currentProject?.name}
            jobId={null}
          />
        )
      case 'workspace':
        return (
          <WorkspaceView
            project={currentProject}
          />
        )
      case 'mcp':
        return <MCPView project={currentProject} />
      default:
        return (
          <DesignerView
            project={currentProject}
            run={currentRun}
            onBackToLanding={handleBackToLanding}
            onRegisterActions={setWorkflowActions}
          />
        )
    }
  }

  // Runs overview — account-wide, flow-agnostic, no project required
  if (showRunsOverview) {
    return (
      <>
        <PipelineRunsView
          accountId={runsAccountId}
          onBack={() => setShowRunsOverview(false)}
          onOpenSettings={() => setShowSettings(true)}
          onOpenHelp={() => setShowHelp(true)}
          userProfile={profile}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </>
    )
  }

  // Model Training Dashboard — platform-level tool, no project required
  if (mode === 'model-training') {
    return (
      <>
        <ModelTrainingView
          onBack={() => {
            setMode('workflow')
            if (!currentProject) { handleBackToLanding() }
          }}
          onOpenSettings={() => setShowSettings(true)}
          onOpenHelp={() => setShowHelp(true)}
          userProfile={profile}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </>
    )
  }

  // ETL Pipeline Builder — platform-level tool, no project required
  if (mode === 'etl') {
    return (
      <>
        <EtlDesignerView
          onBack={() => {
            setMode('workflow')
            if (!currentProject) { handleBackToLanding() }
          }}
          onOpenSettings={() => setShowSettings(true)}
          onOpenHelp={() => setShowHelp(true)}
          userProfile={profile}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </>
    )
  }

  // Schema Conversion Tool — platform-level tool, no project required
  if (mode === 'schema') {
    return (
      <>
        <SchemaToolView
          onBack={() => {
            setMode('workflow')
            if (!currentProject) { handleBackToLanding() }
          }}
          onOpenSettings={() => setShowSettings(true)}
          onOpenHelp={() => setShowHelp(true)}
          userProfile={profile}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </>
    )
  }

  // CodeX Job Monitor — platform-level tool, no project required
  if (mode === 'job-monitor') {
    return (
      <>
        <JobMonitorView
          onBack={() => {
            setMode('workflow')
            if (!currentProject) { handleBackToLanding() }
          }}
          onOpenSettings={() => setShowSettings(true)}
          onOpenHelp={() => setShowHelp(true)}
          userProfile={profile}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </>
    )
  }

  // No project selected → Landing
  if (!currentProject) {
    return (
      <>
        <LandingView
          onProjectSelect={handleProjectSelect}
          onGuidedStart={handleGuidedStart}
          onRunsClick={handleRunsClick}
          onOpenReports={() => openReports()}
          onModelTraining={() => setMode('model-training')}
          onEtlTools={() => setMode('etl')}
          onSchemaTools={() => setMode('schema')}
          onJobMonitor={() => setMode('job-monitor')}
          onOpenSettings={() => setShowSettings(true)}
          onOpenHelp={() => setShowHelp(true)}
          projectError={projectError}
          onClearProjectError={() => setProjectError(null)}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </>
    )
  }

  // Brief routing probe — project was just clicked, we're checking state
  if (projectRouting) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', flexDirection: 'column', gap: 12,
        background: 'var(--bg-primary, #0f1117)', color: 'var(--text-secondary, #8b92a5)',
        fontFamily: 'var(--font-mono, monospace)', fontSize: 13,
      }}>
        <Loader2 size={24} style={{ opacity: 0.5, animation: 'spin 1s linear infinite' }} />
        <div>Opening {currentProject?.name}…</div>
      </div>
    )
  }

  // Generate Documentation screen — accessible from any nav context
  if (mode === 'generate-docs') {
    return (
      <>
        <GenerateDocsView
          project={currentProject}
          caJobId={docCaJobId}
          accountId={currentProject?.account}
          onBack={handleBackToLanding}
          onBackToProjects={handleBackToLanding}
          onGoToAnalyze={() => {
            setGuidedIngestResult(null)
            setCurrentRun({ _isAnalyze: true })
            setMode('guided-analyze')
          }}
          onGoToExtractBizLogic={(jobId) => { setDocCaJobId(jobId || null); setMode('extract-biz') }}
          onGoToDecomposeCode={() => setMode('decompose-code')}
          onGoToServiceGeneration={() => setMode('service-generation')}
          onGoToJavaPkg={(jobIds) => { setSvcGenJobIds(jobIds || []); setRfneJobId(null); setMode('journey-jpkg') }}
          onGoToRefineCode={() => setMode('refine-code')}
          onGoToRefactorCode={() => setMode('refactor-code')}
          onOpenSettings={() => setShowSettings(true)}
          onOpenHelp={() => setShowHelp(true)}
          onOpenReports={() => openReports({
            accountId: currentProject?.account,
            appName:   currentProject?.name,
          })}
          userProfile={profile}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </>
    )
  }

  // Decompose Code screen
  if (mode === 'decompose-code') {
    return (
      <>
        <DecomposeCodeView
          project={currentProject}
          caJobId={docCaJobId}
          accountId={currentProject?.account}
          onBack={() => { setCurrentRun(null); setMode('workflow') }}
          onBackToProjects={handleBackToLanding}
          onGoToAnalyze={() => {
            setGuidedIngestResult(null)
            setCurrentRun({ _isAnalyze: true })
            setMode('guided-analyze')
          }}
          onGoToGenerateDocs={() => setMode('generate-docs')}
          onGoToServiceGeneration={(ctx) => { setDcApprovedJobId(ctx?.approvedJobId || null); setMode('service-generation') }}
          onGoToExtractBizLogic={(jobId) => { setDocCaJobId(jobId || null); setMode('extract-biz') }}
          onGoToJavaPkg={(jobIds) => { setSvcGenJobIds(jobIds || []); setRfneJobId(null); setMode('journey-jpkg') }}
          onGoToRefineCode={() => setMode('refine-code')}
          onGoToRefactorCode={() => setMode('refactor-code')}
          onOpenSettings={() => setShowSettings(true)}
          onOpenHelp={() => setShowHelp(true)}
          onOpenReports={() => openReports({
            accountId: currentProject?.account,
            appName:   currentProject?.name,
          })}
          userProfile={profile}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </>
    )
  }

  // Service Generation screen (Journey handoff from Decompose Code)
  if (mode === 'service-generation') {
    return (
      <>
        <GuidedServiceGenerationView
          project={currentProject}
          approvedJobId={dcApprovedJobId}
          onBack={() => { setCurrentRun(null); setMode('workflow') }}
          onBackToProjects={handleBackToLanding}
          onGoToAnalyze={() => {
            setGuidedIngestResult(null)
            setCurrentRun({ _isAnalyze: true })
            setMode('guided-analyze')
          }}
          onGoToDecomposeCode={() => setMode('decompose-code')}
          onGoToGenerateDocs={() => setMode('generate-docs')}
          onGoToExtractBizLogic={(jobId) => { setDocCaJobId(jobId || null); setMode('extract-biz') }}
          onGoToJavaPkg={(jobIds) => { setSvcGenJobIds(jobIds || []); setRfneJobId(null); setMode('journey-jpkg') }}
          onGoToRefineCode={() => setMode('refine-code')}
          onGoToRefactorCode={() => setMode('refactor-code')}
          onOpenSettings={() => setShowSettings(true)}
          onOpenHelp={() => setShowHelp(true)}
          onOpenReports={() => openReports({
            accountId: currentProject?.account,
            appName:   currentProject?.name,
          })}
          userProfile={profile}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </>
    )
  }

  // Journey Java Packaging
  if (mode === 'journey-jpkg') {
    // Mock quality report preview
    if (jpkgShowReport) {
      return <JpkgReportMockView onBack={() => setJpkgShowReport(false)} />
    }

    // Real view — svcGenJobIds populated (came from Refine Code Build/Package or other flow)
    if (svcGenJobIds.length > 0 || pfltJobId) {
      return (
        <>
          <GuidedJavaPackagingView
            project={currentProject}
            svcGenJobIds={svcGenJobIds}
            refineCodeJobId={rfneJobId}
            preFlightJobId={pfltJobId}
            onBack={() => {
              setSvcGenJobIds([])
              setPfltJobId(null)
              setMode('refine-code')
            }}
            onBackToProjects={handleBackToLanding}
            onGoToAnalyze={() => {
              setGuidedIngestResult(null)
              setCurrentRun({ _isAnalyze: true })
              setMode('guided-analyze')
            }}
            onGoToDecomposeCode={() => setMode('decompose-code')}
            onGoToServiceGeneration={() => setMode('service-generation')}
            onGoToGenerateDocs={() => setMode('generate-docs')}
            onGoToExtractBizLogic={(jobId) => { setDocCaJobId(jobId || null); setMode('extract-biz') }}
            onGoToRefineCode={() => setMode('refine-code')}
            onGoToRefactorCode={() => setMode('refactor-code')}
            onOpenSettings={() => setShowSettings(true)}
            onOpenHelp={() => setShowHelp(true)}
            onOpenReports={() => openReports({
              accountId: currentProject?.account,
              appName:   currentProject?.name,
            })}
            userProfile={profile}
          />
          <SettingsModal
            isOpen={showSettings}
            onClose={() => setShowSettings(false)}
          />
          {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
        </>
      )
    }

    // Fallback gate — no source job IDs (arrived from Journey menu directly)
    return (
      <>
        <div style={{
          minHeight: '100vh', background: 'var(--bg-primary, #0f1117)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'inherit',
        }}>
          <div style={{
            background: 'var(--bg-secondary, #161b27)',
            border: '1px solid var(--border-color, #2a2d3a)',
            borderRadius: '16px', padding: '48px 52px',
            maxWidth: '480px', width: '100%', textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: '14px',
              background: 'rgba(74,144,217,0.1)',
              border: '1px solid rgba(74,144,217,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 24px',
            }}>
              <Package size={26} color="#4a90d9" />
            </div>
            <h2 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 600, color: 'var(--text-primary, #e2e8f0)' }}>
              Build / Package
            </h2>
            <p style={{ margin: '0 0 8px', fontSize: 14, color: 'var(--text-secondary, #94a3b8)', lineHeight: 1.6 }}>
              Build & Package requires a validated compile from the Refine Code Workbench.
            </p>
            <p style={{ margin: '0 0 32px', fontSize: 13, color: 'var(--text-muted, #64748b)', lineHeight: 1.6 }}>
              Open Refine Code, confirm your code compiles cleanly, then use the
              <strong style={{ color: 'var(--text-secondary, #94a3b8)' }}> Build / Package</strong> button to continue.
            </p>
            <button
              onClick={() => setMode('refine-code')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '11px 24px', borderRadius: '8px',
                background: '#4a90d9', border: 'none', color: '#fff',
                fontSize: 14, fontWeight: 500, cursor: 'pointer',
                marginBottom: 16,
              }}
            >
              Go to Refine Code <ArrowRight size={15} />
            </button>
            <div style={{ marginBottom: 8 }}>
              <button
                onClick={() => setJpkgShowReport(true)}
                style={{
                  background: 'none', border: '1px solid var(--border-color, #2a2d3a)',
                  color: 'var(--text-secondary, #94a3b8)',
                  fontSize: 12.5, cursor: 'pointer', padding: '7px 16px',
                  borderRadius: '7px', display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                <BarChart2 size={13} /> Preview Build Quality Report
              </button>
            </div>
            <div>
              <button
                onClick={() => setMode('workflow')}
                style={{
                  background: 'none', border: 'none',
                  color: 'var(--text-muted, #64748b)',
                  fontSize: 13, cursor: 'pointer', padding: '4px 8px',
                }}
              >
                ← Back to Workflow
              </button>
            </div>
          </div>
        </div>
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
      </>
    )
  }

  // Extract Business Logic screen
  if (mode === 'extract-biz') {
    return (
      <>
        <ExtractBizLogicView
          project={currentProject}
          caJobId={docCaJobId}
          accountId={currentProject?.account}
          onBack={() => { setCurrentRun(null); setMode('workflow') }}
          onBackToProjects={handleBackToLanding}
          onGoToAnalyze={() => {
            setGuidedIngestResult(null)
            setCurrentRun({ _isAnalyze: true })
            setMode('guided-analyze')
          }}
          onGoToGenerateDocs={() => setMode('generate-docs')}
          onGoToDecomposeCode={() => setMode('decompose-code')}
          onGoToServiceGeneration={() => setMode('service-generation')}
          onGoToJavaPkg={(jobIds) => { setSvcGenJobIds(jobIds || []); setRfneJobId(null); setMode('journey-jpkg') }}
          onGoToRefineCode={() => setMode('refine-code')}
          onGoToRefactorCode={() => setMode('refactor-code')}
          onOpenSettings={() => setShowSettings(true)}
          onOpenHelp={() => setShowHelp(true)}
          onOpenReports={() => openReports({
            accountId: currentProject?.account,
            appName:   currentProject?.name,
          })}
          userProfile={profile}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </>
    )
  }

  // Refactor Code screen — 5-step flow including embedded Refine Code (Step 5)
  if (mode === 'refactor-code') {
    return (
      <>
        <RefactorCodeView
          project={currentProject}
          onBack={() => setMode('service-generation')}
          onBackToProjects={handleBackToLanding}
          onGoToAnalyze={() => {
            setGuidedIngestResult(null)
            setCurrentRun({ _isAnalyze: true })
            setMode('guided-analyze')
          }}
          onGoToExtractBizLogic={(jobId) => { setDocCaJobId(jobId || null); setMode('extract-biz') }}
          onGoToDecomposeCode={() => setMode('decompose-code')}
          onGoToServiceGeneration={() => setMode('service-generation')}
          onGoToJavaPkg={(jobIds, rfneId) => {
            setSvcGenJobIds(jobIds || [])
            setRfneJobId(rfneId || null)
            setMode('journey-jpkg')
          }}
          onGoToRefineCode={() => setMode('refine-code')}
          onOpenSettings={() => setShowSettings(true)}
          onOpenHelp={() => setShowHelp(true)}
          onOpenReports={() => openReports({
            accountId: currentProject?.account,
            appName:   currentProject?.name,
          })}
          userProfile={profile}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </>
    )
  }

  // Refine Code screen — VS Code-style Refine Code Workbench
  if (mode === 'refine-code') {
    return (
      <>
        <RefineCodeWorkspace
          project={currentProject}
          onBack={() => setMode('service-generation')}
          onGoToJavaPkg={(jobIds, preFlightId) => {
            setSvcGenJobIds(jobIds || [])
            setPfltJobId(preFlightId || null)
            setRfneJobId(null)
            setMode('journey-jpkg')
          }}
          onModeChange={setMode}
          onBackToProjects={handleBackToLanding}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </>
    )
  }

  // Project selected but no run chosen → Transformation Runs list
  if (!currentRun) {
    return (
      <>
        <RunsView
          project={currentProject}
          onBack={handleBackToLanding}
          onRunSelect={handleRunSelect}
          onNewRun={handleNewRun}
          onGuidedAnalyze={handleOpenGuidedAnalyze}
          onOpenReports={({ runId = '', stage = '' } = {}) => openReports({
            accountId: currentProject?.account,
            appName: currentProject?.name,
            runId,
            stage,
          })}
          onOpenSettings={() => setShowSettings(true)}
          onOpenHelp={() => setShowHelp(true)}
          onGenerateDocs={(jobId) => { setDocCaJobId(jobId || null); setMode('generate-docs') }}
          onExtractBizLogic={(jobId) => { setDocCaJobId(jobId || null); setMode('extract-biz') }}
          onDecomposeCode={(jobId) => { if (jobId) setDocCaJobId(jobId); setMode('decompose-code') }}
          onGoToServiceGeneration={() => setMode('service-generation')}
          onGoToRefactorCode={() => setMode('refactor-code')}
          onGoToRefineCode={() => setMode('refine-code')}
          onGoToJavaPkg={(jobIds) => { setSvcGenJobIds(jobIds || []); setRfneJobId(null); setMode('journey-jpkg') }}
          onJobMonitor={() => setMode('job-monitor')}
          userProfile={profile}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </>
    )
  }

  // Guided mode — focused upload stage after AI setup
  if (mode === 'guided') {
    return (
      <>
        <GuidedUploadView
          project={currentProject}
          onBack={handleBackToLanding}
          onBackToProjects={handleBackToLanding}
          onUploadComplete={handleGuidedUploadComplete}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
      </>
    )
  }

  // IDMS Analyze — IDMS-specific dashboards (DML Operations, Schema, Data Architecture, etc.)
  if (mode === 'idms-analyze') {
    const idmsAnalyzeBack = currentRun?._fromProjectCard
      ? handleBackToLanding
      : handleGuidedAnalyzeBackToRuns
    return (
      <>
        <IdmsAnalyzeView
          project={currentProject}
          onBack={idmsAnalyzeBack}
          onBackToProjects={handleBackToLanding}
          onOpenSettings={() => setShowSettings(true)}
          onOpenHelp={() => setShowHelp(true)}
          userProfile={profile}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
      </>
    )
  }

  // Guided Analyze — review extracted assets before confirming the pipeline
  if (mode === 'guided-analyze') {
    // Context-aware back:
    //   fresh guided upload → back to Landing (part of AI setup flow)
    //   opened from project card click → back to Landing
    //   opened from RunsView (ingestResult null) → back to RunsView
    const guidedAnalyzeBack = guidedIngestResult
      ? handleBackToLanding
      : currentRun?._fromProjectCard
        ? handleBackToLanding
        : handleGuidedAnalyzeBackToRuns
    return (
      <>
        <GuidedAnalyzeView
          project={currentProject}
          ingestResult={guidedIngestResult}
          onBack={guidedAnalyzeBack}
          onBackToProjects={handleBackToLanding}
          onConfirm={handleGuidedAnalysisConfirm}
          onOpenSettings={() => setShowSettings(true)}
          onOpenHelp={() => setShowHelp(true)}
          onOpenReports={() => openReports({
            accountId: currentProject?.account,
            appName:   currentProject?.name,
            // no runId — Analyze context only; Reports will resolve the latest run itself
          })}
          onGenerateDocs={(jobId) => { setDocCaJobId(jobId || null); setMode('generate-docs') }}
          onExtractBizLogic={(jobId) => { setDocCaJobId(jobId || null); setMode('extract-biz') }}
          onDecomposeCode={(jobId) => { if (jobId) setDocCaJobId(jobId); setMode('decompose-code') }}
          onGoToServiceGeneration={() => setMode('service-generation')}
          onGoToJavaPkg={(jobIds) => { setSvcGenJobIds(jobIds || []); setRfneJobId(null); setMode('journey-jpkg') }}
          onGoToRefineCode={() => setMode('refine-code')}
          onGoToRefactorCode={() => setMode('refactor-code')}
          userProfile={profile}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
      </>
    )
  }

  // Standard mode — standalone view with its own topbar (no global Navbar)
  if (mode === 'standard') {
    return (
      <>
        <StandardView
          project={currentProject}
          run={currentRun}
          onBack={handleBackToRuns}
          onBackToProjects={handleBackToLanding}
          onSwitchToAdvanced={() => setMode('workflow')}
          onOpenSettings={() => setShowSettings(true)}
          onOpenHelp={() => setShowHelp(true)}
          onOpenReports={() => openReports({
            accountId: currentProject?.account,
            appName: currentProject?.name,
            runId: currentRun?.workflow_run_id,
          })}
          userProfile={profile}
        />
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </>
    )
  }

  // Project + run selected → main workflow app
  return (
    <div className="app">
      <Navbar
        mode={mode}
        onModeChange={setMode}
        project={currentProject}
        onBackToLanding={handleBackToLanding}
        onOpenSettings={() => setShowSettings(true)}
        onOpenS3Browser={() => setShowS3Browser(true)}
        onOpenHelp={() => setShowHelp(true)}
        userProfile={profile}
        onRunWorkflow={workflowActions.run}
        onSaveWorkflow={workflowActions.save}
      />
      <WorkerHealthBanner />
      <div className="main-container">
        {renderView()}
      </div>
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
      <S3BrowserModal
        isOpen={showS3Browser}
        onClose={() => setShowS3Browser(false)}
        onOpenSettings={() => setShowSettings(true)}
        project={currentProject}
      />
      {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
    </div>
  )
}

// Wrapper — checks URL before mounting any hooks.
// ?standalone=reports               → render ReportsView directly in this tab
// ?standalone=refine-workbench-mock → render RefineCodeWorkbenchMock in isolation (design mock)
// ?standalone=refine-code-v2        → render RefineCodeView in isolation (new wired workbench, preview only)
function App() {
  if (window.location.search.includes('standalone=reports')) {
    return <ReportsView />
  }
  if (window.location.search.includes('standalone=refine-workbench-mock')) {
    return <RefineCodeWorkbenchMock />
  }
  if (window.location.search.includes('standalone=refine-code-v2')) {
    // Dev preview only — renders the new Refine Code workbench in isolation.
    // The existing refine-code route is unchanged. This does not affect any flow.
    const projectFromUrl = (() => {
      try {
        const p = new URLSearchParams(window.location.search)
        return {
          account: p.get('account_id') || '',
          name:    p.get('app_name')   || '',
        }
      } catch { return { account: '', name: '' } }
    })()
    return (
      <RefineCodeView
        project={projectFromUrl}
        onBack={() => window.history.back()}
        onGoToJavaPkg={(ids, rfneId) => {
          alert(`Continue to Build/Package — svcgen_job_ids: ${ids.join(', ')} rfne: ${rfneId}`)
        }}
        onOpenSettings={() => {}}
        onOpenHelp={() => {}}
      />
    )
  }
  return <AppInner />
}

export default App
